import { useCallback, useMemo } from "react"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"

const POWER_CUBE = "company_experiment_power_calculation"

// The weekly power-calc DAG evaluates each (days, control_size) cell with
// bayes_sc at these levels; `ci_max` is the smallest cumulative lift (in
// revenue currency) the design could detect under them.
const POWER_LEVEL = "80"
const CONFIDENCE_LEVEL = "90"
const MODEL = "bayes_sc"

export type ExperimentPowerEstimate = {
  /** Minimum detectable cumulative lift over the test window, in currency. */
  minDetectableLift: number
  powerPct: number
  confidencePct: number
}

/**
 * Pre-computed statistical power for candidate designs, keyed by
 * (duration days, control share). Cells exist only where the power DAG has
 * run them for this company — `lookup` returns null otherwise, and the
 * caller should say so rather than hide the card.
 */
export default function useExperimentPowerGrid() {
  const { resultSet, isLoading } = useCubeQuery({
    dimensions: [`${POWER_CUBE}.days`, `${POWER_CUBE}.control_size`],
    measures: [`${POWER_CUBE}.ci_max`],
    filters: [
      { member: `${POWER_CUBE}.model`, operator: "equals", values: [MODEL] },
      {
        member: `${POWER_CUBE}.power`,
        operator: "equals",
        values: [POWER_LEVEL],
      },
      {
        member: `${POWER_CUBE}.confidence`,
        operator: "equals",
        values: [CONFIDENCE_LEVEL],
      },
    ],
  } satisfies TCubeQuery)

  const grid = useMemo(() => {
    const cells = new Map<string, number>()
    for (const row of resultSet?.tablePivot() ?? []) {
      const days = Number(row[`${POWER_CUBE}.days`])
      const controlPct = Math.round(
        Number(row[`${POWER_CUBE}.control_size`]) * 100,
      )
      const mde = Number(row[`${POWER_CUBE}.ci_max`])
      if (Number.isFinite(days) && Number.isFinite(mde)) {
        cells.set(`${days}:${controlPct}`, mde)
      }
    }
    return cells
  }, [resultSet])

  const lookup = useCallback(
    (
      durationDays: number,
      controlPercentage: number,
    ): ExperimentPowerEstimate | null => {
      const mde = grid.get(`${durationDays}:${controlPercentage}`)
      if (mde === undefined) {
        return null
      }
      return {
        minDetectableLift: mde,
        powerPct: Number(POWER_LEVEL),
        confidencePct: Number(CONFIDENCE_LEVEL),
      }
    },
    [grid],
  )

  return { lookup, isLoading, hasAny: grid.size > 0 }
}
